import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Copy, DollarSign, Sliders, ExternalLink, Receipt, Upload, UserPlus, X, Loader2, AlertTriangle, CheckCircle2, Pencil, Link2, Search } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inp = "border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white";
const PAY_TONE = { "Paid in Full": "bg-emerald-100 text-emerald-700", "Balance Due": "bg-amber-100 text-amber-700" };
const REQ_TONE = { Sent: "bg-blue-100 text-blue-700", Resent: "bg-indigo-100 text-indigo-700", "Not Sent": "bg-slate-100 text-slate-500" };
const origin = () => window.location.origin;

export default function AdminWwbaBalances({ eventId }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [extFor, setExtFor] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wwba/balances/summary", { params: { event_id: eventId } });
      setSummary(data.summary); setItems(data.items);
    } catch { toast.error("Failed to load balances"); } finally { setLoading(false); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const sendRequest = async (r) => {
    try {
      await api.post(`/admin/wwba/registrations/${r.id}/balance/send`, null, { params: { origin: origin() } });
      toast.success("Balance request queued (mock in Preview)"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copyLink = async (r) => {
    try {
      const { data } = await api.post(`/admin/wwba/registrations/${r.id}/balance/link`, null, { params: { action: "get", origin: origin() } });
      await navigator.clipboard.writeText(data.link); toast.success("Payment link copied"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const S = summary || {};
  return (
    <div data-testid="wwba-balances">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
        <SumCard label="Total Players" value={S.total_players ?? "—"} testid="sum-total-players" />
        <SumCard label="Paid in Full" value={S.paid_in_full ?? "—"} tone="text-emerald-600" testid="sum-paid" />
        <SumCard label="Balances Outstanding" value={S.outstanding ?? "—"} tone="text-amber-600" testid="sum-outstanding" />
        <SumCard label="Total Revenue" value={money(S.total_revenue_cents)} testid="sum-revenue" />
        <SumCard label="Total Collected" value={money(S.total_collected_cents)} tone="text-emerald-600" testid="sum-collected" />
        <SumCard label="Total Outstanding" value={money(S.total_outstanding_cents)} tone="text-[#E63946]" testid="sum-owed" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="wwba-add-legacy-btn" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><UserPlus className="w-4 h-4" /> Add Legacy</button>
        <button data-testid="wwba-import-csv-btn" onClick={() => setCsvOpen(true)} className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Upload className="w-4 h-4" /> Import CSV</button>
        <button data-testid="wwba-send-all-btn" onClick={() => setSendAllOpen(true)} className="inline-flex items-center gap-1 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Send className="w-4 h-4" /> Send All Outstanding Requests</button>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["Player", "Parent", "Source", "Total", "Paid", "Balance", "Request", "Payment", "Actions"].map((h) => <th key={h} className="px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No billable registrations yet.</td></tr>
              : items.map((r) => {
                const owes = (r.balance_remaining_cents || 0) > 0;
                return (
                  <tr key={r.id} data-testid={`wwba-bal-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-[#0A192F] whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.parent_name ? <>{r.parent_name}<div className={`text-[11px] ${r.parent_email ? "text-slate-400" : "text-rose-500"}`}>{r.parent_email || "⚠ no email — click Edit"}</div></> : <span className="inline-flex items-center gap-1 text-rose-500 text-[11px]"><AlertTriangle className="w-3 h-3" />{r.parent_email || "no contact — click Edit"}</span>}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${r.source === "Legacy" ? "bg-purple-100 text-purple-700" : "bg-sky-100 text-sky-700"}`}>{r.source}</span></td>
                    <td className="px-3 py-2 font-data">{money(r.total_cost_cents)}</td>
                    <td className="px-3 py-2 font-data text-emerald-700">{money(r.total_paid_cents)}</td>
                    <td className="px-3 py-2 font-data font-semibold text-[#0A192F]">{money(r.balance_remaining_cents)}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${REQ_TONE[r.request_status] || "bg-slate-100"}`}>{r.request_status}</span>{r.balance_send_count > 0 && <span className="text-[10px] text-slate-400 ml-1">×{r.balance_send_count}</span>}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${PAY_TONE[r.payment_status] || "bg-slate-100"}`}>{r.payment_status}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <IconBtn testid={`wwba-edit-${r.id}`} title="Edit / Link Player & Contact" onClick={() => setEditFor(r)}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                        {owes && <IconBtn testid={`wwba-send-${r.id}`} title={!r.parent_email ? "Add a parent email first (Edit)" : r.balance_send_count > 0 ? "Resend Balance Request" : "Send Balance Request"} onClick={() => r.parent_email ? sendRequest(r) : (toast.error("No parent email — click Edit to add one"), setEditFor(r))}><Send className={`w-3.5 h-3.5 ${!r.parent_email ? "text-slate-300" : ""}`} /></IconBtn>}
                        {owes && <IconBtn testid={`wwba-copy-${r.id}`} title="Copy Payment Link" onClick={() => copyLink(r)}><Copy className="w-3.5 h-3.5" /></IconBtn>}
                        {owes && <IconBtn testid={`wwba-ext-${r.id}`} title="Record External Payment" onClick={() => setExtFor(r)}><DollarSign className="w-3.5 h-3.5" /></IconBtn>}
                        <IconBtn testid={`wwba-adjust-${r.id}`} title="Adjust Balance" onClick={() => setAdjustFor(r)}><Sliders className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn testid={`wwba-history-${r.id}`} title="View Payment History" onClick={() => setHistoryFor(r)}><Receipt className="w-3.5 h-3.5" /></IconBtn>
                        {r.player?.slug && <a href={`/prospects/${r.player.slug}`} target="_blank" rel="noreferrer" title="View Player" className="p-1.5 border border-slate-200 rounded-sm text-slate-500 hover:bg-slate-100"><ExternalLink className="w-3.5 h-3.5" /></a>}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {addOpen && <AddLegacyModal eventId={eventId} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
      {csvOpen && <CsvImportModal eventId={eventId} onClose={() => setCsvOpen(false)} onDone={() => { setCsvOpen(false); load(); }} />}
      {sendAllOpen && <SendAllModal eventId={eventId} onClose={() => setSendAllOpen(false)} onDone={() => { setSendAllOpen(false); load(); }} />}
      {extFor && <ExternalPaymentModal reg={extFor} onClose={() => setExtFor(null)} onDone={() => { setExtFor(null); load(); }} />}
      {adjustFor && <AdjustModal reg={adjustFor} onClose={() => setAdjustFor(null)} onDone={() => { setAdjustFor(null); load(); }} />}
      {historyFor && <HistoryModal reg={historyFor} onClose={() => setHistoryFor(null)} />}
      {editFor && <EditContactModal reg={editFor} onClose={() => setEditFor(null)} onDone={() => { setEditFor(null); load(); }} />}
    </div>
  );
}

const SumCard = ({ label, value, tone, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-sm p-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-data text-xl mt-1 ${tone || "text-[#0A192F]"}`}>{value}</div>
  </div>
);
const IconBtn = ({ children, title, onClick, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick} className="p-1.5 border border-slate-200 rounded-sm text-slate-600 hover:bg-slate-100 hover:text-[#0A192F]">{children}</button>
);
const Modal = ({ title, onClose, children, testid, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
    <div data-testid={testid} className={`bg-white rounded-sm w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl uppercase">{title}</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
      {children}
    </div>
  </div>
);
const L = ({ children }) => <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 mt-3">{children}</label>;

function AddLegacyModal({ eventId, onClose, onDone }) {
  const [f, setF] = useState({ player_first_name: "", player_last_name: "", date_of_birth: "", parent_first_name: "", parent_last_name: "", parent_email: "", parent_cell: "", amount_paid: "500", payment_date: "", existing_player_id: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.player_first_name || !f.player_last_name) return toast.error("Player first & last name required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wwba/legacy", {
        event_id: eventId, player_first_name: f.player_first_name, player_last_name: f.player_last_name,
        date_of_birth: f.date_of_birth || null, parent_first_name: f.parent_first_name || null, parent_last_name: f.parent_last_name || null,
        parent_email: f.parent_email || null, parent_cell: f.parent_cell || null,
        amount_paid_cents: f.amount_paid ? Math.round(parseFloat(f.amount_paid) * 100) : null,
        payment_date: f.payment_date || null, existing_player_id: f.existing_player_id || null, notes: f.notes || null,
      });
      toast.success(`Legacy registration added (${data.match_reason})`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add Legacy Registration" onClose={onClose} testid="wwba-add-legacy-modal">
      <p className="text-xs text-slate-500 -mt-2 mb-2">For players who paid $500 on CanadianBaseballRankings.com. Defaults: Total $750, Paid $500.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><L>Player First *</L><input data-testid="legacy-first" className={`${inp} w-full`} value={f.player_first_name} onChange={(e) => set("player_first_name", e.target.value)} /></div>
        <div><L>Player Last *</L><input data-testid="legacy-last" className={`${inp} w-full`} value={f.player_last_name} onChange={(e) => set("player_last_name", e.target.value)} /></div>
        <div><L>DOB</L><input type="date" className={`${inp} w-full`} value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
        <div><L>Existing Player ID</L><input className={`${inp} w-full`} value={f.existing_player_id} onChange={(e) => set("existing_player_id", e.target.value)} /></div>
        <div><L>Parent First</L><input className={`${inp} w-full`} value={f.parent_first_name} onChange={(e) => set("parent_first_name", e.target.value)} /></div>
        <div><L>Parent Last</L><input className={`${inp} w-full`} value={f.parent_last_name} onChange={(e) => set("parent_last_name", e.target.value)} /></div>
        <div><L>Parent Email</L><input data-testid="legacy-parent-email" className={`${inp} w-full`} value={f.parent_email} onChange={(e) => set("parent_email", e.target.value)} /></div>
        <div><L>Parent Cell</L><input className={`${inp} w-full`} value={f.parent_cell} onChange={(e) => set("parent_cell", e.target.value)} /></div>
        <div><L>Amount Previously Paid ($)</L><input className={`${inp} w-full`} value={f.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} /></div>
        <div><L>Payment Date</L><input type="date" className={`${inp} w-full`} value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} /></div>
      </div>
      <L>Notes</L><textarea className={`${inp} w-full`} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      <button data-testid="legacy-submit" onClick={submit} disabled={busy} className="mt-4 w-full bg-[#0A192F] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">{busy ? "Adding…" : "Add Registration"}</button>
    </Modal>
  );
}

// -------- CSV parsing --------
function parseCsv(text) {
  const rows = [];
  let row = [], cur = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { row.push(cur); cur = ""; }
      else if (ch === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
      else if (ch === "\r") { /* skip */ }
      else cur += ch;
    }
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}
const HEADER_MAP = {
  "player first name": "player_first_name", "first name": "player_first_name", "player first": "player_first_name",
  "player last name": "player_last_name", "last name": "player_last_name", "player last": "player_last_name",
  "parent first name": "parent_first_name", "parent first": "parent_first_name",
  "parent last name": "parent_last_name", "parent last": "parent_last_name",
  "parent email": "parent_email", "email": "parent_email",
  "parent cell": "parent_cell", "cell": "parent_cell", "phone": "parent_cell",
  "amount previously paid": "amount_paid", "amount paid": "amount_paid", "amount": "amount_paid",
  "payment date": "payment_date",
  "existing player id": "existing_player_id", "player id": "existing_player_id",
  "notes": "notes", "note": "notes",
  "dob": "date_of_birth", "date of birth": "date_of_birth",
};
function toRows(matrix) {
  if (!matrix.length) return [];
  const headers = matrix[0].map((h) => HEADER_MAP[(h || "").trim().toLowerCase()] || null);
  return matrix.slice(1).map((cells) => {
    const o = {};
    headers.forEach((key, idx) => { if (key) o[key] = (cells[idx] || "").trim(); });
    if (o.amount_paid) o.amount_paid_cents = Math.round(parseFloat(o.amount_paid) * 100) || null;
    return o;
  });
}

function CsvImportModal({ eventId, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    const parsed = toRows(parseCsv(text));
    if (!parsed.length) return toast.error("No rows found. Check your CSV headers.");
    setRows(parsed);
    try {
      const { data } = await api.post("/admin/wwba/legacy/preview", { event_id: eventId, rows: parsed });
      setPreview(data);
    } catch (er) { toast.error(er?.response?.data?.detail || "Preview failed"); }
  };
  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    const importRows = preview.rows.filter((r) => r.confidence !== "error").map((r) => ({
      ...r.row, action: r.player ? "match" : "new", player_id: r.player?.id || null,
    }));
    try {
      const { data } = await api.post("/admin/wwba/legacy/import", { event_id: eventId, rows: importRows });
      toast.success(`Imported ${data.created}, skipped ${data.skipped}`); onDone();
    } catch (er) { toast.error(er?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  const C = preview?.counts || {};
  const tone = { matched: "text-emerald-600", review: "text-amber-600", new: "text-sky-600", error: "text-rose-600" };
  return (
    <Modal title="Import Legacy CSV" onClose={onClose} testid="wwba-csv-modal" wide>
      {!preview ? (
        <div>
          <p className="text-xs text-slate-500 mb-3">Columns: Player First Name, Player Last Name, Parent First Name, Parent Last Name, Parent Email, Parent Cell, Amount Previously Paid, Payment Date, Existing Player ID, Notes, DOB. Defaults: Total $750, Paid $500 if blank.</p>
          <input data-testid="wwba-csv-file" type="file" accept=".csv" onChange={onFile} className="text-sm" />
        </div>
      ) : (
        <div>
          <div className="flex gap-4 text-sm mb-3 flex-wrap">
            <span className={tone.matched}>Matched: <strong data-testid="csv-matched">{C.matched || 0}</strong></span>
            <span className={tone.review}>Needs Review: <strong data-testid="csv-review">{C.review || 0}</strong></span>
            <span className={tone.new}>New Players: <strong data-testid="csv-new">{C.new || 0}</strong></span>
            {C.error ? <span className={tone.error}>Errors (skipped): <strong>{C.error}</strong></span> : null}
          </div>
          <div className="border border-slate-200 rounded-sm overflow-y-auto max-h-[45vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500"><tr>{["Player", "Parent Email", "Paid", "Match", "Reason"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.index} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-semibold">{r.row.player_first_name} {r.row.player_last_name}</td>
                    <td className="px-2 py-1.5 text-slate-500">{r.row.parent_email || "—"}</td>
                    <td className="px-2 py-1.5">{r.row.amount_paid ? `$${r.row.amount_paid}` : "$500"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`font-bold uppercase ${tone[r.confidence]}`}>
                        {r.confidence === "review" ? <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Review</span> : r.confidence}
                      </span>
                      {r.player && <div className="text-[10px] text-slate-400">→ {r.player.name}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => { setPreview(null); setRows(null); }} className="flex-1 border border-slate-300 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">Back</button>
            <button data-testid="wwba-csv-import" onClick={commit} disabled={busy} className="flex-1 bg-[#E63946] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">{busy ? "Importing…" : `Import ${preview.rows.filter((r) => r.confidence !== "error").length}`}</button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Rows marked "Review" will create a new profile unless an existing player was matched. Confirm before importing to avoid duplicates.</p>
        </div>
      )}
    </Modal>
  );
}

function SendAllModal({ eventId, onClose, onDone }) {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/wwba/balance/send-preview", { params: { event_id: eventId } }).then(({ data }) => setPrev(data)).catch(() => toast.error("Failed to load")); }, [eventId]);
  const send = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wwba/balance/send-all", { event_id: eventId, mode }, { params: { origin: origin() } });
      toast.success(`Sent ${data.sent} · skipped ${data.skipped_already_sent} already-sent · ${data.skipped_no_email} no email`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };
  return (
    <Modal title="WWBA Final Balance Request" onClose={onClose} testid="wwba-send-all-modal">
      {!prev ? <div className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <div>
          <div className="space-y-1 text-sm">
            <Line l="Players with balances" v={prev.players_with_balance} />
            <Line l="Total outstanding" v={money(prev.total_outstanding_cents)} />
            <Line l="Not previously sent" v={prev.not_previously_sent} />
            <Line l="Previously sent" v={prev.previously_sent} />
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Emails are MOCKED in Preview — no real email is sent. Existing valid payment links are reused.</p>
          <div className="flex flex-col gap-2 mt-4">
            <button data-testid="send-all-unsent" onClick={() => send("unsent")} disabled={busy || prev.not_previously_sent === 0} className="w-full bg-[#0A192F] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40">Send Only to Not Previously Sent ({prev.not_previously_sent})</button>
            <button data-testid="send-all-everyone" onClick={() => send("all")} disabled={busy || prev.players_with_balance === 0} className="w-full bg-[#E63946] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40">Send to Everyone With a Balance ({prev.players_with_balance})</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
const Line = ({ l, v }) => <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{l}</span><span className="font-semibold text-[#0A192F]">{v}</span></div>;

function ExternalPaymentModal({ reg, onClose, onDone }) {
  const [f, setF] = useState({ amount: ((reg.balance_remaining_cents || 0) / 100).toString(), payment_type: "Final Balance", source: "E-Transfer", payment_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    const cents = Math.round(parseFloat(f.amount) * 100);
    if (!cents || cents <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/admin/wwba/registrations/${reg.id}/external-payment`, {
        amount_cents: cents, payment_type: f.payment_type, source: f.source,
        payment_method: "External", payment_date: f.payment_date || null, note: f.note || null,
      });
      toast.success("External payment recorded"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Record External Payment" onClose={onClose} testid="wwba-ext-modal">
      <p className="text-xs text-slate-500 -mt-2">{reg.first_name} {reg.last_name} · Balance {money(reg.balance_remaining_cents)}</p>
      <L>Amount ($) *</L><input data-testid="ext-amount" className={`${inp} w-full`} value={f.amount} onChange={(e) => set("amount", e.target.value)} />
      <L>Payment Type</L>
      <select className={`${inp} w-full`} value={f.payment_type} onChange={(e) => set("payment_type", e.target.value)}>
        <option>Final Balance</option><option>Deposit</option><option>Adjustment</option>
      </select>
      <L>Source / Method</L><input className={`${inp} w-full`} value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="E-Transfer, Cash, Cheque…" />
      <L>Payment Date</L><input type="date" className={`${inp} w-full`} value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} />
      <L>Note</L><input className={`${inp} w-full`} value={f.note} onChange={(e) => set("note", e.target.value)} />
      <button data-testid="ext-submit" onClick={submit} disabled={busy} className="mt-4 w-full bg-[#0A192F] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Record Payment"}</button>
    </Modal>
  );
}

function AdjustModal({ reg, onClose, onDone }) {
  const [total, setTotal] = useState(((reg.total_cost_cents || 0) / 100).toString());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const cents = Math.round(parseFloat(total) * 100);
    if (isNaN(cents) || cents < 0) return toast.error("Enter a valid total");
    setBusy(true);
    try {
      await api.post(`/admin/wwba/registrations/${reg.id}/adjust`, { total_cost_cents: cents, note: note || null });
      toast.success("Balance adjusted"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Adjust Balance" onClose={onClose} testid="wwba-adjust-modal">
      <p className="text-xs text-slate-500 -mt-2">{reg.first_name} {reg.last_name} · Paid {money(reg.total_paid_cents)}. Adjust the total event cost (scholarship, correction). Balance recalculates from payments.</p>
      <L>New Total Event Cost ($) *</L><input data-testid="adjust-total" className={`${inp} w-full`} value={total} onChange={(e) => setTotal(e.target.value)} />
      <L>Reason / Note</L><input className={`${inp} w-full`} value={note} onChange={(e) => setNote(e.target.value)} />
      <button data-testid="adjust-submit" onClick={submit} disabled={busy} className="mt-4 w-full bg-[#0A192F] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Apply Adjustment"}</button>
    </Modal>
  );
}

function EditContactModal({ reg, onClose, onDone }) {
  const [f, setF] = useState({
    parent_name: reg.parent_name || "", parent_email: reg.parent_email || "",
    parent_cell: reg.parent_cell || "", player_cell: "",
  });
  const [linked, setLinked] = useState(reg.player || null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get("/players", { params: { q, limit: 8 } }).then(({ data }) => setResults(data.items || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const pickPlayer = async (p) => {
    setLinked(p); setResults([]); setQ("");
    // pull the player's private contact so the admin can see/keep it
    try {
      const { data } = await api.get(`/players/${p.id}`);
      setF((prev) => ({
        ...prev,
        parent_name: prev.parent_name || data.parent_name || "",
        parent_email: prev.parent_email || data.parent_email || "",
        parent_cell: prev.parent_cell || data.parent_cell || "",
      }));
    } catch { /* profile fetch optional */ }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/wwba/registrations/${reg.id}/contact`, {
        parent_name: f.parent_name || null, parent_email: f.parent_email || null,
        parent_cell: f.parent_cell || null, player_cell: f.player_cell || null,
        link_player_id: linked?.id || null,
      });
      toast.success("Contact updated"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Edit Contact & Link Player" onClose={onClose} testid="wwba-edit-modal">
      <p className="text-xs text-slate-500 -mt-2">{reg.first_name} {reg.last_name} · {reg.source}</p>

      <L>Linked Player Account</L>
      {linked ? (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm">
          <span className="inline-flex items-center gap-1 text-emerald-700"><Link2 className="w-4 h-4" />{linked.first_name} {linked.last_name}{linked.graduation_year ? ` · ${linked.graduation_year}` : ""}</span>
          <button data-testid="wwba-unlink" onClick={() => setLinked(null)} className="text-xs text-slate-400 hover:text-rose-500">Unlink</button>
        </div>
      ) : (
        <div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2.5" />
            <input data-testid="wwba-edit-player-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing player by name…" className={`${inp} w-full pl-8`} />
          </div>
          {results.length > 0 && (
            <div className="mt-1 border border-slate-200 rounded-sm max-h-40 overflow-y-auto divide-y divide-slate-100">
              {results.map((p) => (
                <button key={p.id} data-testid={`wwba-edit-pick-${p.id}`} onClick={() => pickPlayer(p)} className="w-full text-left py-2 px-2 hover:bg-slate-50 text-sm flex justify-between">
                  <span className="font-medium">{p.first_name} {p.last_name}</span><span className="text-slate-400 text-xs">{p.graduation_year} · {p.primary_position}</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-1">Linking pulls the player's parent contact automatically.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><L>Parent Name</L><input data-testid="edit-parent-name" className={`${inp} w-full`} value={f.parent_name} onChange={(e) => set("parent_name", e.target.value)} /></div>
        <div><L>Parent Cell</L><input className={`${inp} w-full`} value={f.parent_cell} onChange={(e) => set("parent_cell", e.target.value)} /></div>
      </div>
      <L>Parent Email (balance requests go here)</L>
      <input data-testid="edit-parent-email" className={`${inp} w-full`} value={f.parent_email} onChange={(e) => set("parent_email", e.target.value)} placeholder="parent@example.com" />
      <button data-testid="edit-save" onClick={save} disabled={busy} className="mt-4 w-full bg-[#0A192F] text-white py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Save Contact"}</button>
    </Modal>
  );
}

function HistoryModal({ reg, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/wwba/registrations/${reg.id}/payments`).then(({ data }) => setData(data)).catch(() => toast.error("Failed")); }, [reg.id]);
  return (
    <Modal title="Payment History" onClose={onClose} testid="wwba-history-modal" wide>
      {!data ? <div className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div><div className="font-semibold text-[#0A192F]">{data.registration.name}</div><div className="text-xs text-slate-400">{data.registration.source}</div></div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm ${PAY_TONE[data.registration.payment_status]}`}>{data.registration.payment_status}</span>
          </div>
          <table className="w-full text-sm border border-slate-200 rounded-sm">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500"><tr>{["Type", "Amount", "Source", "Method", "Status", "Date"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
            <tbody>
              {data.payments.length === 0 ? <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">No payments</td></tr>
                : data.payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-semibold">{p.payment_type}</td>
                    <td className="px-2 py-1.5 font-data">{money(p.amount_cents)}</td>
                    <td className="px-2 py-1.5">{p.source}</td>
                    <td className="px-2 py-1.5 text-slate-500">{p.payment_method}</td>
                    <td className="px-2 py-1.5">{p.status === "paid" ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" />Paid</span> : <span className="text-slate-400">{p.status}</span>}</td>
                    <td className="px-2 py-1.5 text-slate-400">{(p.payment_date || "").slice(0, 10)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="flex justify-between mt-3 text-sm font-semibold"><span>Total Paid: {money(data.registration.total_paid_cents)}</span><span>Balance: {money(data.registration.balance_remaining_cents)}</span></div>
        </div>
      )}
    </Modal>
  );
}
